import type { RouteRecordRaw } from 'vue-router'

import type { Owner } from '@backmarket/nuxt-module-logger/types'

import { ROUTE_NAMES } from '~/constants/routes'

import translations from './pages/Options.translations'

function onboardingRoutes(): RouteRecordRaw[] {
  return [
    {
      path: '/bo-seller/onboarding/options',
      component: () => import('~/scopes/options/pages/Options.vue'),
      meta: {
        autonomousBannerPageName: 'onboarding-options',
        owners: ['bot-squad-seller-experience-front'],
      },
      name: ROUTE_NAMES.ONBOARDING.OPTIONS.INDEX,
      redirect: {
        name: ROUTE_NAMES.ONBOARDING.OPTIONS.ADDRESSES,
      },
      children: [
        {
          component: () =>
            import('~/scopes/options/pages/tabs/Addresses/Addresses.vue'),
          name: ROUTE_NAMES.ONBOARDING.OPTIONS.ADDRESSES,
          path: 'addresses',
          meta: {
            autonomousBannerPageName: 'onboarding-options.addresses',
            owners: ['bot-squad-seller-experience-front'],
          },
        },
        {
          name: ROUTE_NAMES.ONBOARDING.OPTIONS.SHIPPING,
          component: () =>
            import('~/scopes/options/pages/tabs/Shipping/Shipping.vue'),
          path: 'shipping',
          meta: {
            autonomousBannerPageName: 'onboarding-options.shipping',
            tabItem: {
              label: translations.tabShipping,
            },
            owners: ['bot-squad-seller-experience-front'] as Owner[],
          },
        },
        {
          component: () =>
            import('~/scopes/options/pages/tabs/Payment/Payment.vue'),
          name: ROUTE_NAMES.ONBOARDING.OPTIONS.PAYMENT,
          path: 'payment',
          meta: {
            autonomousBannerPageName: 'onboarding-options.payment',
            owners: ['bot-squad-seller-experience-front'],
          },
        },
        {
          component: () =>
            import('~/scopes/options/pages/tabs/Pricing/Pricing.vue'),
          name: ROUTE_NAMES.ONBOARDING.OPTIONS.PRICING,
          path: 'pricing',
          meta: {
            autonomousBannerPageName: 'onboarding-options.pricing',
            owners: ['bot-squad-seller-experience-front'],
          },
        },
        {
          component: () =>
            import('~/scopes/options/pages/tabs/Integrations/Integrations.vue'),
          name: ROUTE_NAMES.ONBOARDING.OPTIONS.INTEGRATIONS,
          path: 'integrations',
          meta: {
            autonomousBannerPageName: 'onboarding-options.integrations',
            owners: ['bot-squad-seller-experience-front'],
          },
        },
        {
          component: () =>
            import('~/scopes/options/pages/tabs/Payment/Hyperwallet/Kyb/Kyb.vue'),
          name: ROUTE_NAMES.ONBOARDING.OPTIONS.HYPERWALLET.KYB,
          path: 'payment/hyperwallet/kyb',
          meta: {
            owners: ['bot-squad-seller-experience-front'],
          },
        },
        {
          component: () =>
            import('~/scopes/options/pages/tabs/Payment/Hyperwallet/TransferMethods/TransferMethods.vue'),
          name: ROUTE_NAMES.ONBOARDING.OPTIONS.HYPERWALLET.TRANSFER_METHODS,
          path: 'payment/hyperwallet/transfer-methods',
          meta: {
            owners: ['bot-squad-seller-experience-front'],
          },
        },
        {
          component: () =>
            import('~/scopes/options/pages/tabs/Payment/Hyperwallet/Confirmation/Confirmation.vue'),
          name: ROUTE_NAMES.ONBOARDING.OPTIONS.HYPERWALLET.CONFIRMATION,
          path: 'payment/hyperwallet/confirmation',
          meta: {
            owners: ['bot-squad-seller-experience-front'],
          },
        },
      ],
    },
  ]
}

const routes = (): RouteRecordRaw[] => {
  return [
    ...onboardingRoutes(),
    {
      component: () => import('~/scopes/options/pages/Options.vue'),
      name: ROUTE_NAMES.OPTIONS.INDEX,
      path: '/bo-seller/options',
      redirect: { name: ROUTE_NAMES.OPTIONS.ADDRESSES },
      meta: {
        autonomousBannerPageName: 'options',
        owners: ['bot-squad-seller-experience-front'],
      },
      children: [
        {
          component: () =>
            import('~/scopes/options/pages/tabs/Addresses/Addresses.vue'),
          name: ROUTE_NAMES.OPTIONS.ADDRESSES,
          path: 'addresses',
          meta: {
            autonomousBannerPageName: 'options.addresses',
            tabItem: {
              label: translations.tabAddresses,
            },
            owners: ['bot-squad-seller-experience-front'],
          },
        },
        {
          component: () =>
            import('~/scopes/options/pages/tabs/Shipping/Shipping.vue'),
          name: ROUTE_NAMES.OPTIONS.SHIPPING,
          path: 'shipping',
          meta: {
            autonomousBannerPageName: 'options.shipping',
            tabItem: {
              label: translations.tabShipping,
            },
            owners: ['bot-squad-seller-experience-front'],
          },
        },
        {
          component: () =>
            import('~/scopes/options/pages/tabs/Payment/Payment.vue'),
          name: ROUTE_NAMES.OPTIONS.PAYMENT,
          path: 'payment',
          meta: {
            autonomousBannerPageName: 'options.payment',
            tabItem: {
              label: translations.tabPayment,
            },
            owners: ['bot-squad-seller-experience-front'],
          },
        },
        {
          component: () =>
            import('~/scopes/options/pages/tabs/Pricing/Pricing.vue'),
          name: ROUTE_NAMES.OPTIONS.PRICING,
          path: 'pricing',
          meta: {
            autonomousBannerPageName: 'options.pricing',
            tabItem: {
              label: translations.tabPricing,
            },
            owners: ['bot-squad-seller-experience-front'],
          },
        },
        {
          component: () =>
            import('~/scopes/options/pages/tabs/Integrations/Integrations.vue'),
          name: ROUTE_NAMES.OPTIONS.INTEGRATIONS,
          path: 'integrations',
          meta: {
            autonomousBannerPageName: 'options.integrations',
            tabItem: {
              label: translations.tabIntegrations,
            },
            owners: ['bot-squad-seller-experience-front'],
          },
        },
        {
          component: () =>
            import('~/scopes/options/pages/tabs/BatchListingCreation/BatchListingCreation.vue'),
          name: ROUTE_NAMES.OPTIONS.BATCH_LISTING_CREATION,
          path: 'batch-listing-creation',
          meta: {
            autonomousBannerPageName: 'options.batch-listing-creation',
            tabItem: {
              label: translations.tabBatchListingCreation,
            },
            owners: ['bot-squad-seller-experience-front'],
          },
        },
        {
          component: () =>
            import('~/scopes/options/pages/tabs/Payment/Hyperwallet/Kyb/Kyb.vue'),
          name: ROUTE_NAMES.OPTIONS.HYPERWALLET.KYB,
          path: 'payment/hyperwallet/kyb',
          meta: {
            owners: ['bot-squad-seller-experience-front'],
          },
        },
        {
          component: () =>
            import('~/scopes/options/pages/tabs/Payment/Hyperwallet/TransferMethods/TransferMethods.vue'),
          name: ROUTE_NAMES.OPTIONS.HYPERWALLET.TRANSFER_METHODS,
          path: 'payment/hyperwallet/transfer-methods',
          meta: {
            owners: ['bot-squad-seller-experience-front'],
          },
        },
        {
          component: () =>
            import('~/scopes/options/pages/tabs/Payment/Hyperwallet/Confirmation/Confirmation.vue'),
          name: ROUTE_NAMES.OPTIONS.HYPERWALLET.CONFIRMATION,
          path: 'payment/hyperwallet/confirmation',
          meta: {
            owners: ['bot-squad-seller-experience-front'],
          },
        },
      ],
    },
  ]
}

export default routes
